/**
 * Updates validation UI for mobile field - sets browser validation messages (orange tooltips) only.
 * Does not display red error messages or borders.
 * @param {HTMLElement} inputElement The input field to style.
 * @param {string} errorMessage Error message (empty string to clear).
 */ function updateValidationUI(inputElement, errorMessage) {
    const isInvalid = errorMessage.length > 0;
    if (inputElement) {
        inputElement.classList.remove('is-invalid', 'is-valid');
        inputElement.removeAttribute('aria-invalid');
        if (typeof inputElement.setCustomValidity === 'function') inputElement.setCustomValidity(isInvalid ? errorMessage || 'Invalid field.' : '');
    }
}
/**
 * Helper function to handle initial validation behavior.
 * Exposes existing invalid values immediately while avoiding premature error display.
 * @param {HTMLElement} inputElement The input field to validate.
 * @param {Function} validationFn The validation function to call.
 * @param {Object} interactionState Object with hasInteracted property to modify.
 */ function handleInitialValidation(inputElement, validationFn, interactionState) {
    const initialValidation = validationFn(false);
    if (!initialValidation) {
        const hasValue = inputElement.value.trim() !== "";
        if (hasValue) {
            // Automatically surface validation when an invalid value already exists.
            interactionState.hasInteracted = true;
            validationFn();
        } else // If the field is empty, just hide any server messages without showing errors yet.
        updateValidationUI(inputElement, "", false);
    }
}
/**
 * Manages mobile number field validation for Australian phone format.
 * Filters input to allow only valid characters, enforces length limits, and validates on submission.
 *
 * @param {string} mobileNoElementId ID of the input element.
 * @param {string} validationMessageElementId ID of the validation message element.
 * @param {string} formElementId ID of the form.
 */ function mobileNoFieldValidation(mobileNoElementId, validationMessageElementId, formElementId) {
    const mobileNoTextBox = document.getElementById(mobileNoElementId);
    const messageElement = document.getElementById(validationMessageElementId);
    const form = document.getElementById(formElementId);
    let hasInteracted = false;
    let hasSubmitted = false;
    if (!mobileNoTextBox) return;
    // Define the maximum and minimum length of the mobile number. As at writing (October 22nd 2025),
    // the maximum length is 20 characters because the database field is set to nvarchar(20).
    const MAX_LENGTH = 20;
    const MIN_LENGTH = 10;
    // Validates the mobile number field based on the length and expected format for an Australian phone number.
    // This function is called when the user submits the form, on input events and on paste events.
    const validateMobileNo = function(applyVisualStyling = true) {
        const currentValue = mobileNoTextBox.value;
        const shouldDisplayError = hasInteracted || hasSubmitted;
        let errorMessage = "";
        if (currentValue.trim() === '') {
            updateValidationUI(mobileNoTextBox, "");
            return true; // Return true so that the orange validation native to the browser is displayed when Mobile is empty. 
        }
        // Simplified regex-based validation
        // Check for multiple plus signs
        if ((currentValue.match(/\+/g) || []).length > 1) errorMessage = "Please enter a valid Work Phone number.";
        else if (currentValue.indexOf('+') > 0) errorMessage = "Please enter a valid Work Phone number.";
        else if ((currentValue.match(/\(/g) || []).length > 1 || (currentValue.match(/\)/g) || []).length > 1) errorMessage = "Please enter a valid Work Phone number.";
        else if (currentValue.includes('(') && !currentValue.includes(')') || !currentValue.includes('(') && currentValue.includes(')')) errorMessage = "Please enter a valid Work Phone number.";
        else if (currentValue.includes('(') && currentValue.includes(')') && currentValue.indexOf('(') > currentValue.indexOf(')')) errorMessage = "Please enter a valid Work Phone number.";
        else if (!/^[+\d()]+$/.test(currentValue)) errorMessage = "Please enter a valid Work Phone number.";
        else if (currentValue.length > MAX_LENGTH) errorMessage = `Work Phone exceeds maximum length of ${MAX_LENGTH} characters.`;
        else {
            const allowedLength = currentValue.replace(/[^0-9()+]/g, '').length;
            if (allowedLength < MIN_LENGTH) errorMessage = `Work Phone must be at least ${MIN_LENGTH} characters.`;
        }
        updateValidationUI(mobileNoTextBox, shouldDisplayError ? errorMessage : "");
        return errorMessage.length === 0;
    };
    // Make the validation function globally accessible for AJAX form submission checks
    window.validateMobileNoForForm = validateMobileNo;
    // Filters the phone number input to only allow valid characters.
    // This function is called when the user types or pastes text into the mobile number input.
    const filterPhoneInput = function(value, cursorPosition) {
        let filtered = '';
        let newCursorPosition = cursorPosition;
        let hasOpenParen = false;
        let hasCloseParen = false;
        for(let i = 0; i < value.length && filtered.length < MAX_LENGTH; i++){
            const char = value[i];
            let shouldInclude = false;
            if (char === '+' && filtered.length === 0) shouldInclude = true;
            else if (char === '(' && !hasOpenParen && !hasCloseParen) {
                shouldInclude = true;
                hasOpenParen = true;
            } else if (char === ')' && hasOpenParen && !hasCloseParen) {
                shouldInclude = true;
                hasCloseParen = true;
            } else if (/\d/.test(char)) shouldInclude = true;
            if (shouldInclude) filtered += char;
            else if (i < cursorPosition) newCursorPosition--;
        }
        return {
            filtered,
            newCursorPosition
        };
    };
    // Users can only type into the mobile number field valid characters within the expected
    // length limits and format for an Australian phone number.
    mobileNoTextBox.addEventListener('input', function(e) {
        hasInteracted = true;
        const result = filterPhoneInput(this.value, this.selectionStart);
        if (this.value !== result.filtered) {
            this.value = result.filtered;
            this.setSelectionRange(result.newCursorPosition, result.newCursorPosition);
        }
        validateMobileNo();
    });
    // Prevents the user from pasting more than the maximum number of characters and
    // from pasting invalid characters.
    mobileNoTextBox.addEventListener('paste', function(e) {
        e.preventDefault();
        hasInteracted = true;
        const pastedText = (e.clipboardData || window.clipboardData).getData('text');
        const textBefore = this.value.substring(0, this.selectionStart);
        const textAfter = this.value.substring(this.selectionEnd);
        const newValue = textBefore + pastedText + textAfter;
        const result = filterPhoneInput(newValue, this.selectionStart + pastedText.length);
        this.value = result.filtered;
        const newCursorPos = this.selectionStart + result.filtered.length - textBefore.length - textAfter.length;
        this.setSelectionRange(newCursorPos, newCursorPos);
        validateMobileNo();
    });
    if (form) // Prevents the form submission if the mobile number field is invalid.
    form.addEventListener('submit', function(event) {
        hasSubmitted = true;
        if (!validateMobileNo()) event.preventDefault();
    });
    // Use helper function for initial validation
    handleInitialValidation(mobileNoTextBox, validateMobileNo, {
        hasInteracted
    });
}

//# sourceMappingURL=extrata-register.247429d5.js.map
